import { useCallback, useEffect, useRef, useState } from 'react';
import type { PageProps } from './types';
import { Button, EmptyState, ProgressBar } from '../components';
import {
  describePlan,
  normalizePlan,
  planFromId,
  planToId,
  type EnginePlan,
} from '../services/enginePlan';
import {
  deleteTask,
  getLatestRunLog,
  getTaskProgress,
  listTasks,
  listTasksWithLogs,
  subscribeToDb,
} from '../db';
import type { Task, TaskProgress, TaskStatus } from '../types';
import { formatDuration } from '../services/audioSlicer';
import {
  describeError,
  ingestAudio,
  isFirstPassRunning,
  runFirstPass,
  suggestTaskName,
  type FirstPassProgress,
} from '../services/firstPass';
import { hasGeminiKey } from '../config';

/* ------------------------------------------------------------------ */
/* Local view models                                                   */
/* ------------------------------------------------------------------ */

interface TaskRow {
  task: Task;
  progress: TaskProgress;
}

interface Job {
  /** null while the audio is still being decoded and no Task row exists yet. */
  taskId: string | null;
  progress: FirstPassProgress;
}

const STATUS_LABEL: Record<TaskStatus, string> = {
  new: 'new',
  processing: 'processing',
  review: 'review',
  completed: 'done',
};

/** MediaRecorder containers, best first. The app re-encodes before upload,
 *  so anything the browser can also decode is fine here. */
const RECORDER_MIMES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg;codecs=opus',
  'audio/ogg',
];

function pickRecorderMime(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined;
  return RECORDER_MIMES.find((m) => {
    try {
      return MediaRecorder.isTypeSupported(m);
    } catch {
      return false;
    }
  });
}

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */

/**
 * Tasks tab — bring audio in and run the first pass over it.
 *
 * Upload or record -> `ingestAudio` (decode, create task, Gemini, slice,
 * persist) -> the task lands in `review` and the Verify tab takes over.
 */
export default function TasksPage({ onOpenTask }: PageProps) {
  const [rows, setRows] = useState<TaskRow[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  /** Task ids that have at least one stored pipeline run log. */
  const [logTaskIds, setLogTaskIds] = useState<Set<string>>(() => new Set());

  const [isRecording, setIsRecording] = useState(false);
  const [recSec, setRecSec] = useState(0);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  /* Which engine processes the next upload. Persisted so a reload keeps it. */
  const [plan, setPlan] = useState<EnginePlan>(() => planFromId(localStorage.getItem('lf.engine') ?? undefined));
  const planRef = useRef(plan);
  planRef.current = plan;
  useEffect(() => {
    localStorage.setItem('lf.engine', planToId(plan));
  }, [plan]);
  const setStage = useCallback(
    (patch: Partial<EnginePlan>) => setPlan((p) => normalizePlan({ ...p, ...patch })),
    [],
  );

  const mountedRef = useRef(true);

  const busy = job !== null;

  const refresh = useCallback(async () => {
    const tasks = await listTasks();
    const next = await Promise.all(
      tasks.map(async (task) => ({ task, progress: await getTaskProgress(task.id) })),
    );
    const withLogs = await listTasksWithLogs().catch(() => new Set<string>());
    if (mountedRef.current) {
      setRows(next);
      setLogTaskIds(withLogs);
      setLoaded(true);
    }
  }, []);

  /**
   * Save the newest run log for a task as JSON.
   *
   * This is the artefact to attach to a "the labels do not match the audio"
   * report: it carries the decoded audio properties, Gemini's raw untouched
   * timestamps, the boundaries after each alignment stage with their quality
   * scores, and — the usual culprit — whether the refinement guard bailed.
   */
  const downloadLog = useCallback(async (task: Task) => {
    try {
      const log = await getLatestRunLog(task.id);
      if (!log) {
        setError('No pipeline log stored for this task yet. Run the first pass again to record one.');
        return;
      }
      const blob = new Blob([JSON.stringify(log, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lableforge-log-${task.name.replace(/[^\w.-]+/g, '_')}-${new Date(log.startedAt)
        .toISOString()
        .replace(/[:.]/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Revoking immediately can cancel the download in some browsers.
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not build the debug log.');
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refresh();
    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
      // Switching tabs unmounts this page. Killing the stream without stopping
      // the recorder threw the take away: `onstop` never fired, so the chunks
      // were never turned into a task. Stop the recorder instead and let its own
      // `onstop` close the stream and ingest what was captured.
      const recorder = recorderRef.current;
      if (recorder && recorder.state !== 'inactive') recorder.stop();
      else streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [refresh]);

  // A first pass started here keeps running after a tab switch unmounts this
  // page; without this the list still showed its task as "processing"/stalled
  // long after it had landed in `review`.
  useEffect(() => {
    let queued = false;
    return subscribeToDb(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        void refresh();
      });
    });
  }, [refresh]);

  /* ---------------- first pass ---------------- */

  const onProgress = useCallback((progress: FirstPassProgress) => {
    if (!mountedRef.current) return;
    setJob((prev) => ({ taskId: prev?.taskId ?? null, progress }));
  }, []);

  const startIngest = useCallback(
    async (blob: Blob, name?: string) => {
      setError(null);
      setJob({
        taskId: null,
        progress: { phase: 'decoding', message: 'Decoding audio…', ratio: 0.02 },
      });
      try {
        await ingestAudio(blob, {
          name: name ?? suggestTaskName(blob),
          plan: planRef.current,
          onProgress,
          onTaskCreated: (task) => {
            if (!mountedRef.current) return;
            setJob((prev) => ({
              taskId: task.id,
              progress: prev?.progress ?? { phase: 'decoding', message: 'Starting…', ratio: 0.05 },
            }));
            void refresh();
          },
        });
      } catch (err) {
        if (mountedRef.current) setError(describeError(err));
      } finally {
        if (mountedRef.current) {
          setJob(null);
          void refresh();
        }
      }
    },
    [onProgress, refresh],
  );

  const retry = useCallback(
    async (taskId: string) => {
      setError(null);
      setJob({
        taskId,
        progress: { phase: 'decoding', message: 'Decoding audio…', ratio: 0.02 },
      });
      try {
        await runFirstPass(taskId, { onProgress });
      } catch (err) {
        if (mountedRef.current) setError(describeError(err));
      } finally {
        if (mountedRef.current) {
          setJob(null);
          void refresh();
        }
      }
    },
    [onProgress, refresh],
  );

  /* ---------------- upload ---------------- */

  const onFilePicked = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      event.target.value = ''; // let the same file be picked again
      if (file) void startIngest(file, file.name);
    },
    [startIngest],
  );

  /* ---------------- recording ---------------- */

  const stopRecording = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== 'inactive') recorder.stop();
  }, []);

  const startRecording = useCallback(async () => {
    setError(null);
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setError('This browser cannot record audio. Upload a file instead.');
      return;
    }

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        // keep the raw room sound — background noise is a label we want
        audio: { echoCancellation: true, noiseSuppression: false, autoGainControl: false },
      });
    } catch (err) {
      setError(
        err instanceof Error && err.name === 'NotAllowedError'
          ? 'Microphone access was denied. Allow it in your browser settings, or upload a file.'
          : `Could not open the microphone: ${describeError(err)}`,
      );
      return;
    }

    const mimeType = pickRecorderMime();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    chunksRef.current = [];

    recorder.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
      recorderRef.current = null;
      if (timerRef.current !== null) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setIsRecording(false);

      const type = recorder.mimeType || mimeType || 'audio/webm';
      const blob = new Blob(chunksRef.current, { type });
      chunksRef.current = [];
      if (blob.size === 0) {
        setError('That recording came out empty.');
        return;
      }
      void startIngest(blob, suggestTaskName(blob));
    };

    recorderRef.current = recorder;
    streamRef.current = stream;
    setRecSec(0);
    setIsRecording(true);
    recorder.start();

    timerRef.current = window.setInterval(() => setRecSec((s) => s + 1), 1000);
  }, [startIngest]);

  /* ---------------- delete ---------------- */

  const remove = useCallback(
    async (taskId: string) => {
      setConfirmDeleteId(null);
      await deleteTask(taskId);
      await refresh();
    },
    [refresh],
  );

  /* ---------------- render ---------------- */

  const jobPct = job ? Math.round(job.progress.ratio * 100) : 0;

  return (
    <>
      <header className="page__head">
        <h1 className="page__title">Tasks</h1>
        <span className="page__sub">upload &amp; first pass</span>
      </header>

      {!hasGeminiKey && (
        <div className="card tp-note tp-note--warn">
          <strong className="tp-note__title">No Gemini API key</strong>
          <p className="tp-note__body">
            Add one to <span className="mono">src/config/geminiKey.local.ts</span> and reload. You can
            still upload audio, but the first pass will fail.
          </p>
        </div>
      )}

      {/* ---- capture controls ---- */}
      <div className="col tp-capture">
        <input
          ref={fileInputRef}
          type="file"
          accept="audio/*"
          className="visually-hidden"
          onChange={onFilePicked}
        />
        {/* Engine A/B: everything after words-and-times is identical, so the
            two settings differ only in who produced them. */}
        <div className="col eng">
          <span className="eng__label">pipeline</span>

          <div className="eng__row">
            <span className="eng__stage">words + times</span>
            <div className="eng__grid">
              <button
                type="button"
                className={`eng__opt${plan.base === 'gemini' ? ' is-on' : ''}`}
                onClick={() => setStage({ base: 'gemini' })}
                disabled={busy}
              >
                Gemini + aligner
              </button>
              <button
                type="button"
                className={`eng__opt${plan.base === 'whisperx' ? ' is-on' : ''}`}
                onClick={() => setStage({ base: 'whisperx' })}
                disabled={busy}
              >
                WhisperX
              </button>
            </div>
          </div>

          {plan.base === 'whisperx' && (
            <>
              <div className="eng__row">
                <span className="eng__stage">Gemini re-reads cuts</span>
                <div className="eng__grid">
                  <button
                    type="button"
                    className={`eng__opt${!plan.geminiCuts ? ' is-on' : ''}`}
                    onClick={() => setStage({ geminiCuts: false })}
                    disabled={busy}
                  >
                    no
                  </button>
                  <button
                    type="button"
                    className={`eng__opt${plan.geminiCuts ? ' is-on' : ''}`}
                    onClick={() => setStage({ geminiCuts: true })}
                    disabled={busy}
                  >
                    yes
                  </button>
                </div>
              </div>

              {plan.geminiCuts && (
                <div className="eng__row">
                  <span className="eng__stage">re-cut long clips</span>
                  <div className="eng__grid eng__grid--3">
                    {(['none', 'burst', 'guided'] as const).map((r) => (
                      <button
                        key={r}
                        type="button"
                        className={`eng__opt${plan.recut === r ? ' is-on' : ''}`}
                        onClick={() => setStage({ recut: r })}
                        disabled={busy}
                      >
                        {r}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="eng__row">
                <span className="eng__stage">fillers</span>
                <div className={`eng__grid ${plan.geminiCuts ? 'eng__grid--4' : ''}`}>
                  <button
                    type="button"
                    className={`eng__opt${plan.fillers === 'off' ? ' is-on' : ''}`}
                    onClick={() => setStage({ fillers: 'off' })}
                    disabled={busy}
                  >
                    default
                  </button>
                  <button
                    type="button"
                    className={`eng__opt${plan.fillers === 'bias' ? ' is-on' : ''}`}
                    onClick={() => setStage({ fillers: 'bias' })}
                    disabled={busy}
                  >
                    WhisperX bias
                  </button>
                  {plan.geminiCuts && (
                    <>
                      <button
                        type="button"
                        className={`eng__opt${plan.fillers === 'prompt' ? ' is-on' : ''}`}
                        onClick={() => setStage({ fillers: 'prompt' })}
                        disabled={busy}
                      >
                        Gemini keep
                      </button>
                      <button
                        type="button"
                        className={`eng__opt${plan.fillers === 'force' ? ' is-on' : ''}`}
                        onClick={() => setStage({ fillers: 'force' })}
                        disabled={busy}
                      >
                        force
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}

          <p className="eng__flow mono">{describePlan(plan)}</p>
        </div>

        <Button
          variant="primary"
          size="lg"
          block
          icon="⬆️"
          disabled={busy || isRecording}
          onClick={() => fileInputRef.current?.click()}
        >
          Upload audio
        </Button>

        {isRecording ? (
          <Button variant="danger" size="lg" block icon="⏹" onClick={stopRecording}>
            Stop recording · {formatDuration(recSec)}
          </Button>
        ) : (
          <Button size="lg" block icon="🎙" disabled={busy} onClick={() => void startRecording()}>
            Record with mic
          </Button>
        )}
      </div>

      {/* ---- live job ---- */}
      {job && (
        <div className="card tp-job">
          <div className="row tp-job__head">
            <strong>{job.progress.phase === 'error' ? 'Failed' : 'First pass running'}</strong>
            <span className="spacer" />
            <span className="mono tp-job__pct">{jobPct}%</span>
          </div>
          <ProgressBar value={jobPct} max={100} label={job.progress.message} showCount={false} />
        </div>
      )}

      {/* ---- errors ---- */}
      {error && (
        <div className="card tp-job tp-note--danger" role="alert">
          <div className="row tp-card__top">
            <span aria-hidden="true">⚠️</span>
            <div className="tp-card__main">{error}</div>
          </div>
          <div className="tp-card__bar">
            <Button size="sm" variant="ghost" onClick={() => setError(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* ---- task list ---- */}
      {loaded && rows.length === 0 && !job ? (
        <EmptyState
          icon="🎧"
          title="No tasks yet"
          hint="Upload or record audio to start a labeling task."
        />
      ) : (
        <div>
          {rows.map(({ task, progress }) => {
            const isActive = job?.taskId === task.id;
            // A run started before this page was last mounted is still real —
            // `isFirstPassRunning` outlives the component, so don't call it
            // stalled and don't offer a retry that would race it.
            const running = isActive || isFirstPassRunning(task.id);
            const stalled = task.status === 'processing' && !running;
            const chipClass = stalled ? 'tp-chip--stalled' : `tp-chip--${task.status}`;
            /** Segments marked incorrect that still need a label. */
            const toFix = progress.incorrect - (progress.resolved - progress.correct);

            return (
              <div className="card" key={task.id}>
                <div className="row tp-card__top">
                  <div className="tp-card__main">
                    <div className="tp-card__name" title={task.name}>
                      {task.name}
                    </div>
                    <div className="tp-card__meta">
                      <span className="mono">{formatDuration(task.durationSec)}</span>
                      {progress.total > 0 && (
                        <> · {progress.total} segment{progress.total === 1 ? '' : 's'}</>
                      )}
                      {task.engine && <> · {describePlan(planFromId(task.engine))}</>}
                    </div>
                  </div>
                  <span className={`badge tp-chip ${chipClass}`}>
                    {stalled ? 'stalled' : STATUS_LABEL[task.status]}
                  </span>
                </div>

                {isActive && job && (
                  <div className="tp-card__bar">
                    <ProgressBar
                      value={Math.round(job.progress.ratio * 100)}
                      max={100}
                      label={job.progress.message}
                      showCount={false}
                    />
                  </div>
                )}

                {!isActive && progress.total > 0 && (
                  <div className="tp-card__bar">
                    <ProgressBar value={progress.resolved} max={progress.total} label="Resolved" />
                  </div>
                )}

                {task.error && <p className="tp-card__error">{task.error}</p>}

                <div className="row tp-card__actions">
                  {(task.status === 'new' || stalled) && (
                    <Button
                      size="sm"
                      variant="primary"
                      disabled={busy || running}
                      onClick={() => void retry(task.id)}
                    >
                      {task.error || stalled ? 'Retry first pass' : 'Run first pass'}
                    </Button>
                  )}

                  {/* A task in `review` is either still being verified or has
                      incorrect segments waiting for a label — send the user to
                      whichever queue actually has work for this task. */}
                  {task.status === 'review' &&
                    (progress.pending > 0 ? (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => onOpenTask(task.id, 'verify')}
                      >
                        Verify ({progress.pending} left)
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => onOpenTask(task.id, 'correct')}
                      >
                        {toFix > 0 ? `Correct (${toFix} left)` : 'Correct'}
                      </Button>
                    ))}

                  {task.status === 'completed' && (
                    <Button size="sm" onClick={() => onOpenTask(task.id, 'done')}>
                      Open transcript
                    </Button>
                  )}

                  <span className="spacer" />

                  {/* Pipeline telemetry for the most recent run of this task.
                      Hidden when the task has never been through a first pass,
                      so a fresh upload does not offer an empty download. */}
                  {logTaskIds.has(task.id) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={running}
                      title="Download this task's pipeline debug log (JSON)"
                      aria-label={`Download debug log for ${task.name}`}
                      onClick={() => void downloadLog(task)}
                    >
                      ⬇ debug log
                    </Button>
                  )}

                  {confirmDeleteId === task.id ? (
                    <>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDeleteId(null)}>
                        Cancel
                      </Button>
                      <Button size="sm" variant="danger" onClick={() => void remove(task.id)}>
                        Delete
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={running}
                      aria-label={`Delete ${task.name}`}
                      onClick={() => setConfirmDeleteId(task.id)}
                    >
                      🗑
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
